"use client";

import { Button } from "@pex/ui-web";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import imgCentre from "@/public/images/find-help/FindHelp_Centre_01.webp";
import imgLeft01 from "@/public/images/find-help/FindHelp_Left01.webp";
import imgLeft02 from "@/public/images/find-help/FindHelp_Left02.webp";
import imgLeft03 from "@/public/images/find-help/FindHelp_Left03.webp";
import imgRight01 from "@/public/images/find-help/FindHelp_Right01.webp";
import imgRight02 from "@/public/images/find-help/FindHelp_Right02.webp";
import imgRight03 from "@/public/images/find-help/FindHelp_Right03.webp";

const COLLAGE = {
  left: [
    {
      src: imgLeft01,
      alt: "A woman sitting on a couch, engaged in reading a book, with a coffee cup on the nearby table.",
    },
    {
      src: imgLeft02,
      alt: "Two men standing indoors, smiling for a picture.",
    },
    {
      src: imgLeft03,
      alt: "A man sitting in a chair indoors, with a big smile.",
    },
  ],
  center: {
    src: imgCentre,
    alt: "Three men in black shirts standing together with arms around each other.",
  },
  right: [
    {
      src: imgRight01,
      alt: "A woman holding a microphone, ready to sing, in an indoor setting.",
    },
    {
      src: imgRight02,
      alt: "A group of people smiling and posing for a photo indoors.",
    },
    {
      src: imgRight03,
      alt: "Group members sitting in a circle on chairs on grass.",
    },
  ],
} as const;

interface HeroSectionProps {
  headingParts: ({ text: string; accent?: boolean } | { break: true })[];
  primaryCta: { label: string; href: string };
  secondaryCta: { label: string; href: string };
  subtitle: string;
}

export function HeroSection({
  headingParts,
  subtitle,
  primaryCta,
  secondaryCta,
}: HeroSectionProps) {
  const containerRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Section-scoped scroll (same as Relume's intent, but scoped so it works
  // correctly when there is content below the hero on the page)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Verbatim Relume createTransform — expanded to explicit hooks to satisfy lint rules
  const leftX = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["0vw", "-25vw"] : ["0vw", "-32vw"]
  );
  const centerX = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["0vw", "-25vw"] : ["0vw", "-32vw"]
  );
  const centerWidth = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["50vw", "100vw"] : ["36vw", "100vw"]
  );
  const centerHeight = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["60vh", "100vh"] : ["80vh", "100vh"]
  );
  const rightX = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["0vw", "25vw"] : ["0vw", "32vw"]
  );
  // Border radius collapses to 0 as the image reaches full coverage.
  // Driven off the same scrollYProgress as the size/x transforms — adds a
  // single paint-only animation channel (no extra layout cost).
  const centerRadius = useTransform(scrollYProgress, [0, 1], ["14px", "0px"]);

  return (
    <section
      className="relative h-[250vh] bg-background md:h-[300vh]"
      ref={containerRef}
    >
      {/* Text — in normal document flow, scrolls away naturally */}
      <div className="pt-16 md:pt-24 lg:pt-28">
        <div className="mx-auto w-full max-w-3xl px-4 text-center sm:px-6">
          <h1 className="heading-1 mb-5 w-full font-bold md:mb-6">
            {headingParts.map((part, i) => {
              if ("break" in part) {
                // biome-ignore lint/suspicious/noArrayIndexKey: break elements are static and have no stable id
                return <br className="hidden md:block" key={`br-${i}`} />;
              }
              if (part.accent) {
                return (
                  <span
                    className="font-semibold font-serif italic"
                    key={part.text}
                    style={{
                      background:
                        "linear-gradient(90deg, #D85722 0%, #FEB240 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {part.text}
                  </span>
                );
              }
              return <span key={part.text}>{part.text}</span>;
            })}
          </h1>
          <p className="body-md font-normal text-muted-foreground">
            {subtitle}
          </p>
          <div className="mt-6 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row md:mt-8 md:gap-4">
            <Button
              asChild
              className="w-full sm:w-auto"
              size="lg"
              variant="fancy"
            >
              <Link href={primaryCta.href}>{primaryCta.label}</Link>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto"
              size="lg"
              variant="secondary-fancy"
            >
              <Link href={secondaryCta.href}>{secondaryCta.label}</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="sticky top-0 flex h-screen w-full items-center overflow-hidden">
        <div className="z-10 grid h-[60vh] w-full grid-flow-col grid-cols-[25%_50%_25%] content-center items-center justify-center md:h-[70vh] md:grid-cols-[32%_36%_32%] lg:h-[80vh]">
          {/* Left group */}
          <motion.div
            className="grid grid-flow-col grid-cols-1 items-center justify-items-end gap-4 justify-self-end px-4"
            style={{ x: leftX }}
          >
            <div className="relative hidden md:block md:w-[25vw] lg:w-[20vw]">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.left[0].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 1023px) 25vw, 20vw"
                  src={COLLAGE.left[0].src}
                />
              </div>
            </div>
            <div className="relative grid w-[40vw] grid-cols-1 grid-rows-[auto_auto] gap-4 self-center md:w-[18vw]">
              <div className="relative aspect-square w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.left[1].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 767px) 40vw, 18vw"
                  src={COLLAGE.left[1].src}
                />
              </div>
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.left[2].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 767px) 40vw, 18vw"
                  src={COLLAGE.left[2].src}
                />
              </div>
            </div>
          </motion.div>

          {/* Centre — width + height + x animated (Relume style); border
              radius collapses to 0 as it fills the viewport */}
          <motion.div
            className="relative overflow-hidden rounded-xl"
            style={{
              width: centerWidth,
              height: centerHeight,
              x: centerX,
              borderRadius: centerRadius,
            }}
          >
            <Image
              alt={COLLAGE.center.alt}
              className="object-cover"
              fill
              placeholder="blur"
              priority
              sizes="(max-width: 767px) 100vw, 60vw"
              src={COLLAGE.center.src}
            />
          </motion.div>

          {/* Right group */}
          <motion.div
            className="grid grid-flow-col items-center justify-items-start gap-4 justify-self-start px-4"
            style={{ x: rightX }}
          >
            <div className="relative grid w-[40vw] grid-cols-1 grid-rows-[auto_auto] gap-4 self-center md:w-[18vw]">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.right[0].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 767px) 40vw, 18vw"
                  src={COLLAGE.right[0].src}
                />
              </div>
              <div className="relative aspect-square w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.right[1].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 767px) 40vw, 18vw"
                  src={COLLAGE.right[1].src}
                />
              </div>
            </div>
            <div className="relative hidden md:block md:w-[25vw] lg:w-[20vw]">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-xl">
                <Image
                  alt={COLLAGE.right[2].alt}
                  className="object-cover"
                  fill
                  placeholder="blur"
                  sizes="(max-width: 1023px) 25vw, 20vw"
                  src={COLLAGE.right[2].src}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
